import React, { useState, useEffect } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowLeft, Loader2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BookingPayment from "@/components/app/BookingPayment";

export default function RefundFlow({ patient, onReset, onBack }) {
  const { t, lang } = useLang();
  const [query, setQuery] = useState(patient?.phone || "");
  const [loading, setLoading] = useState(false);
  const [appts, setAppts] = useState([]);
  const [picked, setPicked] = useState(null);
  const [correctDisease, setCorrectDisease] = useState("");
  const [triage, setTriage] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [pickedDoctor, setPickedDoctor] = useState(null);
  const [pickedDept, setPickedDept] = useState(null);
  const [doctors, setDoctors] = useState([]);

  const doLookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const data = await api.lookup(query.trim());
      setAppts(data);
      if (data.length === 0) toast.info("No bookings found for that token/phone.");
    } catch (e) {
      toast.error("Lookup failed.");
    } finally {
      setLoading(false);
    }
  };

  const runTriage = async () => {
    if (!correctDisease.trim()) return;
    setTriageLoading(true);
    try {
      const data = await api.triage({ mode: "disease", text: correctDisease, language: lang });
      setTriage(data);
    } catch (e) {
      toast.error("Triage failed.");
    } finally {
      setTriageLoading(false);
    }
  };

  const selectDept = async (deptId) => {
    setPickedDept(deptId);
    const docs = await api.doctors(deptId);
    setDoctors(docs);
  };

  if (pickedDoctor && picked) {
    return (
      <BookingPayment
        patient={patient}
        doctor={pickedDoctor}
        department={pickedDept}
        consultationType="transfer"
        disease={correctDisease}
        transferOldId={picked.id}
        onReset={onReset}
        onBack={() => setPickedDoctor(null)}
      />
    );
  }

  // After choosing which appointment, show correction + triage + doctor selection
  if (picked) {
    const isRejected = picked.status === "rejected";
    return (
      <div className="mx-auto max-w-4xl py-6">
        <Button variant="ghost" onClick={() => setPicked(null)} data-testid="back-btn" className="mb-4">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
        </Button>
        {!isRejected ? (
          <Card className="border-amber-300 bg-amber-50 p-5" data-testid="not-rejected-notice">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-900">{t("not_rejected_notice")}</p>
            </div>
          </Card>
        ) : (
          <>
            <Card className="mb-6 border-emerald-300 bg-emerald-50 p-5" data-testid="rejected-notice">
              <p className="text-sm text-emerald-900"><Sparkles className="mr-1 inline h-4 w-4" /> {t("rejected_notice")}</p>
            </Card>

            <Card className="mb-6 border-slate-200 bg-white p-6">
              <label className="mb-2 block text-sm font-semibold text-slate-700">{t("correct_disease_label")}</label>
              <div className="flex gap-2">
                <Input value={correctDisease} onChange={(e) => setCorrectDisease(e.target.value)} placeholder={t("correct_disease_placeholder")} data-testid="correct-disease-input" className="h-12" />
                <Button onClick={runTriage} disabled={!correctDisease.trim() || triageLoading} data-testid="refund-triage-btn" className="h-12 bg-sky-600 hover:bg-sky-700">
                  {triageLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                </Button>
              </div>
            </Card>

            {triage && !pickedDept && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid="refund-triage-grid">
                {triage.recommendations.slice(0, 8).map((r) => (
                  <Card key={r.department_id} className="border-slate-200 bg-white p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-bold text-sm">{r.department_name}</h4>
                      <Badge variant="outline">{r.score}%</Badge>
                    </div>
                    <div className="mb-3 h-1.5 rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-sky-500" style={{ width: `${r.score}%` }} />
                    </div>
                    <Button size="sm" onClick={() => selectDept(r.department_id)} data-testid={`refund-select-dept-${r.department_id}`} className="w-full bg-slate-900 hover:bg-slate-800">
                      Select
                    </Button>
                  </Card>
                ))}
              </div>
            )}

            {pickedDept && (
              <div>
                <Button variant="ghost" onClick={() => setPickedDept(null)} data-testid="back-btn" className="mb-4">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
                </Button>
                <h3 className="mb-4 text-lg font-bold">{t("select_doctor")}</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {doctors.map((d) => (
                    <Card key={d.id} className="border-slate-200 bg-white p-5" data-testid={`refund-doctor-${d.id}`}>
                      <div className="font-bold">{d.name}</div>
                      <div className="mt-1 text-sm text-slate-500">₹{d.fee}</div>
                      <Button onClick={() => setPickedDoctor(d)} className="mt-3 w-full bg-sky-600 hover:bg-sky-700">
                        {t("rebook_new")}
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl py-6">
      <Button variant="ghost" onClick={onBack} data-testid="back-btn" className="mb-4">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
      </Button>
      <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("refund_lookup_title")}</h2>
      <Card className="border-slate-200 bg-white p-6">
        <p className="mb-3 text-sm text-slate-600">{t("refund_lookup_desc")}</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="GS-104 or 9876543210" className="pl-10 h-12" data-testid="refund-lookup-input" />
          </div>
          <Button onClick={doLookup} disabled={!query.trim() || loading} data-testid="refund-lookup-btn" className="h-12 bg-sky-600 hover:bg-sky-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("lookup")}
          </Button>
        </div>
      </Card>

      <div className="mt-5 grid gap-3">
        {appts.map((a) => (
          <Card key={a.id} onClick={() => setPicked(a)} className="cursor-pointer border-slate-200 bg-white p-4 transition hover:border-sky-400 hover:shadow" data-testid={`appt-${a.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold">{a.token_id} · {a.doctor_name}</div>
                <div className="text-xs text-slate-500">{a.department} · {a.slot_date} {a.slot_time}</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    waiting: "bg-amber-100 text-amber-800",
    calling: "bg-sky-100 text-sky-800",
    completed: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return <Badge className={colors[status] || "bg-slate-100"}>{status}</Badge>;
}
